import { ConversationStatus } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { ShopperAiIntegrationType } from "@redotech/effect-api-shared/models/shopper-ai/integration/integration";
import Asterisk01Icon from "@redotech/redo-icons/asterisk-01.svg";
import {
  RedoBadge,
  type RedoBadgeSize,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { Schema } from "effect";
import { memo } from "react";
import { ConversationStatusReadable } from "../../ui/conversation-status-ui";
import {
  IntegrationTypeIcons,
  IntegrationTypeReadable,
} from "../../ui/thread-channel-ui";

const statusColorMap: Record<
  ConversationStatus,
  "success" | "gray" | "warning" | "blue"
> = {
  OPEN: "success",
  CLOSED: "gray",
  SNOOZED: "warning",
  IN_PROGRESS: "blue",
  HIDDEN: "gray",
  ARCHIVED: "gray",
  DELETED: "gray",
};

export const StatusBadge = memo(function StatusBadge({
  status,
  size = "sm",
  showIcon = false,
}: {
  status: ConversationStatus;
  size?: RedoBadgeSize;
  showIcon?: boolean;
}) {
  return (
    <RedoBadge
      color={statusColorMap[status]}
      segmentLeading={
        showIcon ? { type: "icon", Icon: Asterisk01Icon } : undefined
      }
      size={size}
      text={ConversationStatusReadable[status]}
    />
  );
});

export const ConversationPriorityReadable = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
  CRITICAL: "Critical",
} as const;

export const CONVERSATION_PRIORITIES = Object.keys(
  ConversationPriorityReadable,
) as (keyof typeof ConversationPriorityReadable)[];

export const ConversationPriority = Schema.Literal(...CONVERSATION_PRIORITIES);
export type ConversationPriority = typeof ConversationPriority.Type;

const priorityColorMap: Record<
  ConversationPriority,
  "gray" | "blue" | "warning" | "error"
> = { LOW: "gray", MEDIUM: "blue", HIGH: "warning", CRITICAL: "error" };

export const PriorityBadge = memo(function PriorityBadge({
  priority,
  size = "sm",
}: {
  priority: ConversationPriority;
  size?: RedoBadgeSize;
}) {
  return (
    <RedoBadge
      color={priorityColorMap[priority]}
      size={size}
      text={ConversationPriorityReadable[priority]}
    />
  );
});

export const ChannelBadge = memo(function ChannelBadge({
  channel,
  size = "xs",
}: {
  channel: ShopperAiIntegrationType;
  size?: RedoBadgeSize;
}) {
  const displayName = IntegrationTypeReadable[channel];
  const Icon = IntegrationTypeIcons[channel];

  return (
    <RedoBadge
      color="gray"
      segmentLeading={{ type: "icon", Icon }}
      size={size}
      text={displayName}
    />
  );
});
